import type { LoaderConfig, MediaPluginConfigs, Source } from '@foundation-player/types';
import { Media } from '../media';
import { PlayerConfig } from '../player-config';
import { PlayerInterface } from '../player-interface';

/**
 * Ensure that the plugin key matches the one given in the config.
 */
export interface ConfigurablePlayerPlugin {
  getKey(): keyof (MediaPluginConfigs & LoaderConfig['pluginConfigs']);
}
export function isConfigurablePlayerPlugin(plugin: unknown): plugin is ConfigurablePlayerPlugin {
  return typeof plugin === 'object' && plugin !== null && 'getKey' in plugin;
}

/**
 * Specifies plugins that offer methods that can be used by clients.
 */
export interface PublicPluginApi<Key extends string, PublicInterface> {
  /**
   * Gives a key the plugin can be identified and retrieved with
   * It must be a unique string and this will be enforced by the player
   */
  getKey(): Key;

  /**
   * Return a public (client) accessible interface to the plugin
   */
  getPlugin(): PublicInterface;
}

export function hasPublicApi(plugin: unknown): plugin is PublicPluginApi<string, unknown> {
  return (
    plugin !== null &&
    typeof plugin === 'object' &&
    'getKey' in plugin &&
    typeof plugin.getKey === 'function' &&
    'getPlugin' in plugin &&
    typeof plugin.getPlugin === 'function'
  );
}

/**
 * PlayerPlugins should have no dependencies to other plugins.
 * Plugins that manipulate the configurations should do this only once.
 * This means when calling plugin hooks multiple times or in a different order the resulting config should be the same.
 */
export interface PlayerPlugin {
  /**
   * Receives the player instance once the player is created.
   * DO NOT OVERRIDE.
   * @internal
   */
  setPlayer: (player: PlayerInterface) => void;

  /**
   * Called after the player and all plugins are created. If the promise is rejected, it will be ignored.
   * All hooks will be executed in parallel.
   */
  playerInitialized?: () => Promise<void>;

  /**
   * Called before the player is created. If the promise is rejected, the player will not be created.
   * This config MUST NOT be cached. Use the applicable player methods instead.
   */
  beforeCreate?: (playerConfig: PlayerConfig, playerId: string) => Promise<PlayerConfig>;

  /**
   * Called before content is loaded and before manifest evaluation. If the promise is rejected, it will be ignored.
   * All hooks will be executed in parallel.
   */
  setupSession?: () => Promise<void>;

  /**
   * Called before beforeLoadMedia to allow all plugins to modify the URL
   * before the first plugin(or the root player) literally starts requesting the URL's target with a HTTP request.
   * If the promise is rejected, the media will not be loaded.
   * The media object MUST NOT be cached. Use the applicable player methods instead!
   */
  prepareMediaSource?: (media: Readonly<Media>) => Promise<Readonly<Source>>;

  // TODO: The name beforeLoadMedia isn't precise. Because it's part of loadMedia itself. What about "beforeLoadingMediaSource"?
  /**
   * Called before a media's source URL is passed to the root player(which is going to load the media) and after all prepareMediaSource hooks resolved.
   * If the promise is rejected, the media will not be loaded.
   * The media object MUST NOT be cached. Use the applicable player methods instead!
   */
  beforeLoadMedia?: (media: Readonly<Media>) => Promise<Readonly<Media>>;

  /**
   * Called before an event is dispatched in the Event Bus.
   *
   * @remarks
   * This hook allows you to cancel the event by returning false.
   *
   * @param event
   */
  beforeDispatch?: (event: Event) => boolean;

  /**
   * Modify the root player prototype before initialization.
   * Used to add modules to the Bitmovin player.
   */
  modifyRootPrototype?: <PlayerPrototype = unknown>(rootPlayer: PlayerPrototype) => Promise<PlayerPrototype>;
  /**
   * Modify the created root player.
   * Used to add adapters to the dash.js player.
   */
  afterCreateRootPlayer?: <RootPlayer = unknown>(rootPlayer: RootPlayer, playerId: string) => Promise<RootPlayer>;

  /**
   * Called on beforeunload event of the window.
   *
   * @remarks
   * You should use this hook to clean up resources like tracking sessions.
   *
   * This is functionally equivalent to calling window.addEventListener('beforeunload', () => {
   *   this.beforeWindowUnload();
   * });
   *
   * This hook exists to avoid some common pitfalls with the beforeunload event.
   * This hook will guarantee that the listener will always receive a void return value.
   * It will also ensure that the listener is removed when the player is destroyed to avoid littering the window object
   * and to avoid memory leaks.
   */
  beforeWindowUnload?: () => void;
}

export abstract class AbstractPlayerPlugin implements PlayerPlugin {
  protected player?: PlayerInterface;

  setPlayer(player: PlayerInterface) {
    this.player = player;
  }
}
